import classNames from 'classnames'
import type {ReactElement} from 'react'

import {TooltipBody, TooltipWrapper} from 'components'

export interface TabSelectorTabData {
  value: string
  label: string
  disabled?: boolean
  disabledTooltip?: string
}

export interface TabSelectorProps {
  activeTab: string
  tabs: TabSelectorTabData[]
  setActiveTab: (index: string) => void
  containerClassName?: string
  className?: string
  disableInactiveTab?: boolean
  /** When any tab ships `disabledTooltip`, anchors use this id with `react-tooltip`. */
  tooltipWrapperId?: string
}

export const TabSelector = ({
  tabs,
  activeTab,
  containerClassName = '',
  className = '',
  setActiveTab,
  disableInactiveTab = false,
  tooltipWrapperId = 'tab-selector-tooltip',
}: TabSelectorProps): ReactElement => {
  const showTabTooltips = tabs.some(tab => tab.disabled && tab.disabledTooltip)

  return (
    <div
      className={classNames(
        {
          'flex border-1 border-gray-400 bg-dope-black': !containerClassName,
        },
        containerClassName,
      )}
    >
      {tabs.map((tab, index) => {
        const isGloballyInactiveDisabled =
          disableInactiveTab && tab.value !== activeTab
        const buttonDisabled = !!tab.disabled || isGloballyInactiveDisabled
        const isDisabledWithTooltip =
          !!tab.disabled && !!tab.disabledTooltip && showTabTooltips
        const disabledTooltipAttrs = isDisabledWithTooltip
          ? {
              'data-tooltip-id': tooltipWrapperId,
              'data-tooltip-content': tab.disabledTooltip,
            }
          : {}
        const isActive = tab.value === activeTab

        const tabClassName = classNames(
          `flex flex-1 items-center justify-center py-2 px-1 text-white text-xs karla`,
          className,
          {
            'border-r border-gray-400': index !== tabs.length - 1,
          },
          buttonDisabled
            ? {
                'font-bold text-cyan-200 bg-gray-600/30 cursor-not-allowed opacity-70':
                  isActive,
                'cursor-not-allowed !text-gray-500 opacity-70': !isActive,
              }
            : {
                'font-bold text-cyan-200 bg-gray-600/30 cursor-default':
                  isActive,
                'cursor-pointer !text-gray-400': !isActive,
              },
        )

        /** Native `disabled` blocks hover in some browsers; tooltips need pointer events (see SegmentedToggle). */
        if (isDisabledWithTooltip) {
          return (
            <span
              key={`tab-selector-${tab.label}`}
              role="tab"
              tabIndex={-1}
              aria-disabled
              aria-pressed={isActive}
              aria-label={tab.label}
              className={tabClassName}
              {...disabledTooltipAttrs}
            >
              {tab.label}
            </span>
          )
        }

        return (
          <button
            role="tab"
            type="button"
            key={`tab-selector-${tab.label}`}
            onClick={() => {
              if (!buttonDisabled) {
                setActiveTab(tab.value)
              }
            }}
            disabled={buttonDisabled}
            className={tabClassName}
            aria-pressed={isActive}
            aria-disabled={buttonDisabled ? true : undefined}
          >
            {tab.label}
          </button>
        )
      })}
      {showTabTooltips ? (
        <TooltipWrapper
          id={tooltipWrapperId}
          place="bottom"
          className="z-100"
          render={({content}) => (
            <TooltipBody classes="shadow-none" noPadding>
              {content}
            </TooltipBody>
          )}
        />
      ) : null}
    </div>
  )
}
